import React, { useState, useCallback, useEffect } from 'react';
import { getApiEndpoint, getApiBaseUrl } from '../../../config/oncorisk-config';
import DetailView from '../detail-view';
import './multi-variant-search.css';
import OncoRiskNetworkGraph from './OncoRiskNetworkGraph';

import { getCookieId } from '../../../WebserverUser';
import { serviceRequest } from '../../../../shared/xomics-webserver-client';

function MultiVariantSearch() {
  const [input, setInput] = useState('');
  const [terms, setTerms] = useState([]);
  const [rawResultsByTerm, setRawResultsByTerm] = useState([]);
  const [displayableResults, setDisplayableResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItemForModal, setSelectedItemForModal] = useState(null);
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  const [enrichedResults, setEnrichedResults] = useState([]);
  const [diseaseTreeData, setDiseaseTreeData] = useState(null);
  const [isLightTheme, setIsLightTheme] = useState(false);

  // Theme detection effect
  useEffect(() => {
    const checkTheme = () => {
      if (typeof document !== 'undefined') {
        const hasLightClass = document.documentElement.classList.contains('ocrp-theme-light');
        setIsLightTheme(hasLightClass);
      }
    };

    checkTheme();

    if (typeof window !== 'undefined') {
      const handleThemeChange = (event) => {
        if (event.detail && typeof event.detail.theme === 'string') {
          setIsLightTheme(event.detail.theme === 'light');
        }
      };

      window.addEventListener('ocrp-theme-change', handleThemeChange);
      return () => window.removeEventListener('ocrp-theme-change', handleThemeChange);
    }
  }, []);

  const handleResultTileClick = (item) => {
    setFocusedNodeId(item.id);
  };


  const addTerm = useCallback(() => {
    const t = input.trim();
    if (!t || terms.includes(t)) {
      setInput('');
      return;
    }
    setTerms((prev) => [...prev, t]);
    setInput('');
  }, [input, terms]);

  const removeTerm = useCallback((idxToRemove) => {
    setTerms((prev) => {
      const newTerms = prev.filter((_, i) => i !== idxToRemove);
      if (newTerms.length === 0) {
        setSearchAttempted(false);
        setRawResultsByTerm([]);
        setDisplayableResults([]);
        setEnrichedResults([]);
        setDiseaseTreeData(null); // <-- ADDITION
      }
      return newTerms;
    });
  }, []);

  const handleSearch = useCallback(async () => {
    setSearchAttempted(true);
    setRawResultsByTerm([]);
    setDisplayableResults([]);
    setEnrichedResults([]);
    setDiseaseTreeData(null); // <-- ADDITION

    if (terms.length === 0) {
      setSearchAttempted(false);
      return;
    }

    setLoading(true);

    const initialSearchResults = [];
    const mainNodeTypes = [
      'features', 'molecularprofiles', 'therapies', 'therapy', 'assertions', 'assertion',
      'evidences', 'evidence', 'phenotypes', 'phenotype', 'sources', 'source', 'diseases',
      'disease', 'variants', 'variant'
    ];

    for (const t of terms) {
      try {
        const resp = await fetch(`${getApiEndpoint('search')}?term=${encodeURIComponent(t)}`);
        if (!resp.ok) throw new Error(`API Error ${resp.status}`);
        const data = await resp.json();
        const filtered = (Array.isArray(data) ? data : []).filter(item => 
            item && mainNodeTypes.includes((item.itemType || '').toLowerCase())
        );
        initialSearchResults.push({ term: t, items: filtered });
      } catch (e) {
        console.error(`Error searching for term "${t}":`, e);
        initialSearchResults.push({ term: t, items: [], error: e.message });
      }
    }
    setRawResultsByTerm(initialSearchResults);

    const uniqueItemsMap = new Map();
    initialSearchResults.forEach(res => {
        res.items.forEach(item => {
            if (item && item.id) {
                uniqueItemsMap.set(item.id, { ...item, originalTerm: res.term });
            }
        });
    });
    
    const allResults = Array.from(uniqueItemsMap.values());
    const numberedResults = allResults.map((item, index) => ({
        ...item,
        chipIndex: index + 1
    }));
    
    setDisplayableResults(numberedResults);

    let networkData = [];
    if (numberedResults.length > 0) {
      const detailEndpointBase = getApiBaseUrl();
      const detailPromises = numberedResults.map(item =>
        fetch(`${detailEndpointBase}/items/${item.itemType}/${item.id}`)
          .then(res => res.ok ? res.json() : Promise.reject(`Failed to fetch details for ${item.name}`))
      );

      try {
        const settledResults = await Promise.allSettled(detailPromises);
        settledResults.forEach((result, originalIndex) => {
          if (result.status === 'fulfilled') {
            networkData.push({
              ...result.value,
              type: numberedResults[originalIndex].itemType,
              chipIndex: numberedResults[originalIndex].chipIndex,
              originalTerm: numberedResults[originalIndex].originalTerm
            });
          }
        });
        setEnrichedResults(networkData);
      } catch (error) {
        console.error("Error fetching detailed data for network graph:", error);
      }
    }

    // ======================== ADDITION START ========================
    // This block runs *after* the main data is fetched and set.
    if (networkData.length > 0) {
        const diseaseNamesToFetch = new Set();
        networkData.forEach(item => {
            if ((item.type || '').toLowerCase().includes('disease')) {
                diseaseNamesToFetch.add(item.name);
            }
            if (item.associated_diseases && Array.isArray(item.associated_diseases)) {
                item.associated_diseases.forEach(disease => {
                    if (disease && disease.name) diseaseNamesToFetch.add(disease.name);
                });
            }
        });

        if (diseaseNamesToFetch.size > 0) {
            try {
                const diseaseNames = Array.from(diseaseNamesToFetch);
                const treeData = await serviceRequest('oncorisk', getCookieId(), '/disease-tree', {
                    method: 'POST',
                    body: JSON.stringify({ diseaseNames })
                });
                setDiseaseTreeData(treeData);
            } catch (treeError) {
                console.error("Error calling /api/disease-tree:", treeError);
            }
        }
    }
    // ======================== ADDITION END ========================

    setLoading(false);
  }, [terms]);

  const openModalWithItem = useCallback((item) => {
    const rawType = item.type || item.itemType || 'features';
    const modalItem = {
      id: item.originalId || item.id,
      name: item.name,
      itemType: rawType === 'evidences' ? 'evidence' : rawType
    };
    setSelectedItemForModal(modalItem);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItemForModal(null);
  }, []);

  const handleNavigationInModal = useCallback((type, id, name) => {
    setSelectedItemForModal({
      itemType: type === 'evidences' ? 'evidence' : type,
      id: id,
      name: name
    });
  }, []);

  const getTermErrors = () => rawResultsByTerm.filter(r => r.error).map(r => ({term: r.term, error: r.error}));

  const exampleSearches = [
    { label: 'BRCA1 + Breast Cancer', terms: ['BRCA1', 'Breast Cancer'] },
    { label: 'TP53 + Lung Cancer', terms: ['TP53', 'Lung Cancer'] },
    { label: 'EGFR + Erlotinib', terms: ['EGFR', 'Erlotinib'] },
    { label: 'KRAS + Colorectal', terms: ['KRAS', 'Colorectal Cancer'] },
  ];

  const handleExampleClick = (exampleTerms) => {
    setTerms(exampleTerms);
    // Trigger search after state update
    setTimeout(() => {
      document.querySelector('.OCRP-mvs-search-btn')?.click();
    }, 50);
  };

  return (
    <div className="OCRP-mvs-page">
      <div className="OCRP-mvs-page-left">
        <div className="OCRP-mvs-page-left-top">
          <div className="OCRP-mvs-add">
         
            <input
              className="OCRP-text-input"
              type="text"
              value={input}
              placeholder="Search anything... (Ex. BRCA1, Tamoxifen, Breast Cancer, rs123456)"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTerm();
                }
              }}
            />
            <button
              className="OCRP-button-add OCRP-mvs-add-btn"
              onClick={(e) => {
                e.preventDefault();
                addTerm();
              }}
              aria-label="Add term"
              disabled={!input.trim()}
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>
        <div className="OCRP-mvs-page-left-middle">
          {terms.length > 0 && (
            <div className="OCRP-mvs-term-list">
              {terms.map((t, idx) => (
                <div key={idx} className="OCRP-mvs-term">
                  <span>{t.toUpperCase()}</span>
                  <button
                    className="OCRP-button-add OCRP-mvs-remove-btn"
                    onClick={() => removeTerm(idx)}
                    aria-label={`Remove term ${t}`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="OCRP-mvs-page-left-bottom">
          <button
            className="OCRP-mvs-search-btn"
            onClick={handleSearch}
            disabled={loading || terms.length === 0}
          >
            {loading ? (
                <i className="fas fa-spinner fa-spin"></i>
            ) : (
                <i className="fas fa-search"></i>
            )}
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>
      
      <div className="OCRP-mvs-page-right">
        {!loading && !searchAttempted && terms.length === 0 ? (
          <div className="OCRP-mvs-empty-state">
            <p className="OCRP-mvs-empty-title">Try an example search</p>
            <div className="OCRP-mvs-example-grid">
              {exampleSearches.map((example, idx) => (
                <button
                  key={idx}
                  className="OCRP-mvs-example-btn"
                  onClick={() => handleExampleClick(example.terms)}
                >
                  <i className="fas fa-search"></i>
                  {example.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="OCRP-mvs-page-right-results">
              {loading && !searchAttempted && <div className="OCRP-mvs-loading">Searching...</div>}
              {!loading && searchAttempted && displayableResults.length === 0 && getTermErrors().length === 0 && (
                <p className="OCRP-mvs-empty">No Results</p>
              )}
              {!loading && getTermErrors().length > 0 && (
                <div className="OCRP-mvs-error-summary">
                  <h4>Search Errors:</h4>
                  {getTermErrors().map(err => (
                    <p key={err.term} className="OCRP-mvs-error">Error for "{err.term}": {err.error}</p>
                  ))}
                </div>
              )}
              {!loading && displayableResults.length > 0 && (
                <div className="OCRP-mvs-result-tile-grid">
                  {displayableResults.map((item) => (
                    <div
                      key={item.id}
                      className="OCRP-mvs-result-tile"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleResultTileClick(item)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleResultTileClick(item); }}
                    >
                      <span className="OCRP-mvs-tile-dot">{item.chipIndex}</span>
                      <div className="OCRP-mvs-tile-content">
                        <span className="OCRP-mvs-tile-name">
                          <div className="OCRP-mvs-tile-main-name">{item.name || item.id}</div>
                          <div className="OCRP-mvs-tile-search-term">Search: {item.originalTerm}</div>
                          <div className="OCRP-mvs-tile-type">Type: {item.itemType}</div>
                        </span>
                      </div>
                      <button
                        className="OCRP-mvs-tile-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          const termIndex = terms.findIndex(term =>
                            displayableResults.find(res => res.id === item.id && res.name.includes(term)));
                          if (termIndex !== -1) {
                            removeTerm(termIndex);
                          }
                        }}
                        aria-label={`Remove ${item.name}`}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="OCRP-mvs-page-right-network">
              <OncoRiskNetworkGraph
                data={enrichedResults}
                diseaseTreeData={diseaseTreeData}
                onNodeClick={openModalWithItem}
                focusedNodeId={focusedNodeId}
                isLightTheme={isLightTheme}
              />
            </div>
          </>
        )}
      </div>
      
      {isModalOpen && selectedItemForModal && (
        <div className="OCRP-mvs-modal-overlay" onClick={closeModal}>
          <div className="OCRP-mvs-modal-content" onClick={(e) => e.stopPropagation()}>
            <DetailView 
              itemType={selectedItemForModal.itemType}
              itemId={selectedItemForModal.id}
              itemName={selectedItemForModal.name} 
              onBack={closeModal} 
              onNavigate={handleNavigationInModal}
              isLightTheme={isLightTheme}
            /> 
          </div>
        </div>
      )}
    </div>
  );
}

export default MultiVariantSearch;
